import {useMemo} from "react";
import {useWatch} from "react-hook-form";
import {PosOrderForm} from "@/data/entities/order";
import {RestaurantDeliveryArea} from "@/data/entities/restaurant";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {useFulfillmentType} from "@widgets/pos/order/hooks/UseFulfillmentType";
import useMenu from "@widgets/pos/order/hooks/MenuProvider";
import {CartTotals, cartToTotals, checkMinimumOrder, MinimumOrderCheck} from "@/data/entities/cart/utils";
import {Dinero, greaterThan, subtract} from "dinero.js";
import {stringToDinero} from "@/utils/MoneyUtils";
import {useExistingOrder} from "@widgets/pos/order/hooks/UseExistingOrder";

/**
 * For an edited order that was paid online: what was paid, and the
 * difference with the new total.
 */
export type OnlinePaymentDifference = {
    paidOnline: Dinero<number>,
    // The new total is higher: to pay extra in person (PIN)
    toPayInPerson: Dinero<number>,
    // The new total is lower: to refund via Mollie
    toRefund: Dinero<number>,
};

const ZERO: Dinero<number> = stringToDinero("0");

const onlinePaymentDifference = (amountPaidOnline: number, total: Dinero<number>): OnlinePaymentDifference => {
    const paidOnline: Dinero<number> = stringToDinero(String(amountPaidOnline));
    return {
        paidOnline,
        toPayInPerson: greaterThan(total, paidOnline) ? subtract(total, paidOnline) : ZERO,
        toRefund: greaterThan(paidOnline, total) ? subtract(paidOnline, total) : ZERO,
    };
};

/**
 * Postal code digits of the address the order is delivered to, to determine
 * the delivery area (fee and minimum order amount).
 */
const deliveryPostalCodeDigits = (
    deliveryAddress: PosOrderForm["deliveryAddress"],
    customer: PosOrderForm["customer"],
    businessDetails: PosOrderForm["businessDetails"],
): string | null => {
    switch (deliveryAddress.type) {
        case "TAKEAWAY":
            return null;
        case "NEW":
            return deliveryAddress.address.postalCodeDigits?.trim() || null;
        case "EXISTING":
            return customer?.deliveryAddresses
                .find(address => address.id === deliveryAddress.id)
                ?.postalCodeDigits.trim() || null;
        case "BUSINESS":
            return businessDetails?.postalCodeDigits?.trim() || null;
    }
};


export type OrderTotals = {
    totals: CartTotals,
    minimumOrder: MinimumOrderCheck,
    deliveryArea: RestaurantDeliveryArea | null,
    onlinePayment: OnlinePaymentDifference | null,
};

const useOrderTotals = (): OrderTotals => {
    const products = useWatch<PosOrderForm, "products">({name: "products"});
    const deliveryAddress = useWatch<PosOrderForm, "deliveryAddress">({name: "deliveryAddress"});
    const customer = useWatch<PosOrderForm, "customer">({name: "customer"});
    const businessDetails = useWatch<PosOrderForm, "businessDetails">({name: "businessDetails"});
    const fulfillmentType = useFulfillmentType();
    const existingOrder = useExistingOrder();
    const {lookup} = useMenu();
    const {dashboardLocation} = useRestaurant();

    return useMemo(() => {
        const postalCodeDigits: string | null = deliveryPostalCodeDigits(deliveryAddress, customer, businessDetails);
        const deliveryArea: RestaurantDeliveryArea | null = fulfillmentType === "DELIVERY" && postalCodeDigits
            ? dashboardLocation.deliveryAreas.find(a => a.postalCode === postalCodeDigits) ?? null
            : null;

        const totals: CartTotals = cartToTotals(
            products, lookup, fulfillmentType, deliveryArea, existingOrder?.tipPercentage ?? 0);
        const minimumOrder: MinimumOrderCheck = checkMinimumOrder(
            totals.subtotal, fulfillmentType, postalCodeDigits, deliveryArea);
        const onlinePayment: OnlinePaymentDifference | null = existingOrder?.amountPaidOnline != null
            ? onlinePaymentDifference(existingOrder.amountPaidOnline, totals.total)
            : null;

        return {totals, minimumOrder, deliveryArea, onlinePayment};
    }, [products, deliveryAddress, fulfillmentType, lookup, dashboardLocation.deliveryAreas, businessDetails,
        customer, existingOrder]);
};

export default useOrderTotals;